import { _s } from '@/locale';
import { BLANK_VALUE } from '@/constants/analytics';
import { PHYSICAL_FEE } from '@/constants/giftcardConstants';
import { track } from '@/helpers/tracking';
import { ProductType } from '@/pages/presentkort/GiftcardCheckout.types';
import { giftCardSaveFailedErrorTypeSchema } from '@/types/analytics/error-types';
import { CheckoutTracking } from '@/types/analytics/checkout';
import { SaveGiftcardResponse } from '@/types/api/services/giftcard';
import type { Catalogue } from '@bokadirekt/tracking-schema/marketplace';
import {
  trackEventTikTok,
  trackMpAnalyticsEvent,
  trackMpPinterestEvent,
  trackMpQuantcastEvent,
  trackMpRevenueGiftcards,
} from './analytics';

export const getGiftcardTotalAmount = ({
  amount,
  quantity,
  isPhysical,
}: {
  amount: number;
  quantity: number;
  isPhysical: boolean;
}) => amount * quantity + (isPhysical ? PHYSICAL_FEE : 0);

export function trackGiftCardSelected(giftCardType: ProductType, company: { id: number; name: string }) {
  track('Gift Card Purchase Selected', {
    gift_card_type: giftCardType,
    place_id: String(company.id),
    place_name: company.name,
  });
}

export function trackGiftcardPurchase(props: CheckoutTracking.Giftcard) {
  const { is_wellness, is_digital, giftcard_total, campaign, giftcard_type, place_id, place_name } =
    props.giftcardProps;
  const selectedPaymentMethod = props.paymentMethodsProps?.selected_payment_method;

  track('Gift Card Purchase Completed', {
    gift_card_type: giftcard_type,
    paid_price: giftcard_total,
    currency: 'sek',
    delivery_type: is_digital ? 'digital' : 'physical',
    ...(campaign && { campaign }),
    ...(selectedPaymentMethod &&
      selectedPaymentMethod !== BLANK_VALUE && { payment_methods_selected: [selectedPaymentMethod] }),
    ...(place_id && { place_id: String(place_id), place_name: place_name }),
  });

  switch (giftcard_type) {
    case 'giftcard':
    case 'wellnesscard': {
      trackMpAnalyticsEvent('gift_card_purchase_success', {
        card_type: is_wellness ? 'wellness' : 'universal',
        amount: giftcard_total,
        value: giftcard_total,
        currency: 'SEK',
      });

      trackMpAnalyticsEvent('gc_purchase_success', {
        gc_type: is_wellness ? 'wellness' : 'universal',
      });

      trackMpRevenueGiftcards({
        value: giftcard_total || 0,
        currency: 'SEK',
        contentName: `UGC${is_wellness ? 'W' : ''}`,
        contentType: 'product',
        description: is_wellness ? 'wellness_card' : 'gift_card',
      });

      trackEventTikTok(`${is_wellness ? 'InitiateCheckout' : 'Purchase'}`, {
        value: giftcard_total, // number. Value of the order or items sold. Example: 100.
        currency: 'SEK', // string. The 4217 currency code. Example: 'USD'.
        contents: [
          {
            content_id: `UGC${is_wellness ? 'W' : ''}`, // string. ID of the product. Example: '1077218'.
            content_type: 'product', // string. Either product or product_group.
            content_name: is_wellness ? 'Wellness card' : 'Universal gift card', // string. The name of the page or product. Example: 'shirt'.
          },
        ],
        content_type: 'product',
        content_category: is_wellness ? 'wellness_card' : 'universal_gift_card',
      });

      trackMpPinterestEvent('Purchase', {
        value: giftcard_total || 0,
        currency: 'SEK',
        property: is_wellness ? 'wellness_card' : 'universal_gift_card',
      });

      trackMpQuantcastEvent([`_fp.event.Purchase_UGC${is_wellness ? 'W' : ''}`], {
        revenue: giftcard_total || 0,
        currency: 'SEK',
      });

      if (!is_wellness) {
        trackMpPinterestEvent('checkout', {
          value: giftcard_total || 0,
          currency: 'SEK',
          property: is_wellness ? 'wellness_card' : 'universal_gift_card',
        });
      }

      return;
    }

    case 'placecard':
    case 'valuecard': {
      const isValueCard = giftcard_type === 'valuecard';

      trackMpAnalyticsEvent('gift_card_purchase_success', {
        card_type: 'individual',
        amount: giftcard_total,
        value: giftcard_total,
        currency: 'SEK',
      });
      trackMpAnalyticsEvent('gc_purchase_success', {
        gc_type: 'individual',
      });
      trackMpRevenueGiftcards({
        value: giftcard_total || 0,
        currency: 'SEK',
        contentName: isValueCard ? 'VC' : 'IGC',
        contentType: 'product',
        description: isValueCard ? 'value_card' : 'place_gift_card',
      });
    }
  }
}

const SAVED_GIFT_CARD_TYPE: Record<
  SaveGiftcardResponse['type'],
  Catalogue['Gift Card Save Completed']['gift_card_type']
> = {
  UGC: 'giftcard',
  UGCW: 'wellnesscard',
  EGC: 'placecard',
  IGC: 'placecard',
};

export function trackGiftcardSaveCompleted(card: SaveGiftcardResponse) {
  const giftCardType = SAVED_GIFT_CARD_TYPE[card.type];

  track('Gift Card Save Completed', {
    gift_card_type: giftCardType,
    card_value: card.amount,
    currency: 'sek',
    initiated_from: 'save_link',
    ...(giftCardType === 'placecard' && card.place && { place_id: String(card.place.id), place_name: card.place.name }),
  });
}

/** `errorCode` is the server's save code; it is sent only if the plan lists it. */
export function trackGiftcardSaveFailed(errorCode?: string) {
  track('Gift Card Save Failed', {
    error_type: giftCardSaveFailedErrorTypeSchema.catch('unknown').parse(errorCode),
    initiated_from: 'save_link',
  });
}

export const getGiftcardErrorMessage = (message?: string) => {
  switch (message) {
    case 'not_found':
      return _s('saveGiftcard.error.notFound');
    case 'expired':
      return _s('saveGiftcard.error.expired');
    case 'empty':
      return _s('saveGiftcard.error.empty');
    case 'already_saved':
      return _s('saveGiftcard.error.alreadySaved');
    case 'already_saved_another_account':
      return _s('saveGiftcard.error.alreadySavedAnotherAccount');
    case 'not_supported':
      return _s('saveGiftcard.error.notSupported');
    case 'invalid_type':
      return _s('saveGiftcard.error.invalidType');
    case 'invalid':
      return _s('saveGiftcard.error.invalid');
    case 'link_use_expired':
      return _s('saveGiftcard.error.linkUseExpired');
    case 'link_already_used':
      return _s('saveGiftcard.error.linkAlreadyUsed');
    default:
      return _s('unknownError');
  }
};
